// 认证相关 API

import request from './request'

export interface AuthCapabilities {
  email_available: boolean
  public_registration_enabled: boolean
  password_reset_enabled: boolean
  turnstile_enabled: boolean
  turnstile_site_key: string
  google_login_enabled: boolean
  google_signup_enabled: boolean
}

export function getAuthCapabilities() {
  return request.get('/auth/capabilities') as Promise<AuthCapabilities>
}

export function exchangeGoogleLogin(exchange_ticket: string, tenant_id = '') {
  return request.post('/auth/google/exchange', {
    exchange_ticket,
    tenant_id: tenant_id || null,
  })
}

export function registerGoogleAccount(
  exchange_ticket: string,
  nickname: string,
  tenant_name: string,
  agreement_confirmed: boolean,
  turnstile_token = '',
) {
  return request.post('/auth/google/register', {
    exchange_ticket,
    nickname,
    tenant_name,
    agreement_confirmed,
    turnstile_token: turnstile_token || null,
  })
}

// 登录：邮箱验证后按目标企业账号的认证方式继续。
export function loginStart(email: string, trusted_device_token = '', turnstile_token = '', force_email_verification = false) {
  return request.post('/auth/login/start', {
    email,
    trusted_device_token: trusted_device_token || null,
    turnstile_token: turnstile_token || null,
    force_email_verification,
  })
}

export function loginVerify(email: string, challenge_id: string, code: string) {
  return request.post('/auth/login/verify', { email, challenge_id, code })
}

export function login(tenant_id: string, login_ticket: string, password: string, remember_device = false) {
  return request.post('/auth/login', { tenant_id, login_ticket, password, remember_device })
}

export function verifyMfaLogin(mfa_ticket: string, code: string) {
  return request.post('/auth/mfa/login/verify', { mfa_ticket, code })
}

export function getMfaStatus() {
  return request.get('/auth/mfa/status')
}

export function sendMfaReauthCode() {
  return request.post('/auth/mfa/reauth/code')
}

export function startMfaSetup(body: { current_password?: string; challenge_id?: string; email_code?: string }) {
  return request.post('/auth/mfa/setup/start', body)
}

export function confirmMfaSetup(setup_token: string, code: string) {
  return request.post('/auth/mfa/setup/confirm', { setup_token, code })
}

export function startMfaEnrollment(mfa_ticket: string) {
  return request.post('/auth/mfa/enrollment/start', { mfa_ticket })
}

export function confirmMfaEnrollment(mfa_ticket: string, setup_token: string, code: string) {
  return request.post('/auth/mfa/enrollment/confirm', { mfa_ticket, setup_token, code })
}

export function regenerateMfaRecoveryCodes() {
  return request.post('/auth/mfa/recovery-codes/regenerate', {})
}

export function disableMfa() {
  return request.post('/auth/mfa/disable', {})
}

export function startMfaReplacement() {
  return request.post('/auth/mfa/replacement/start', {})
}

export function confirmMfaReplacement(setup_token: string, code: string) {
  return request.post('/auth/mfa/replacement/confirm', { setup_token, code })
}

export function getMfaRecoveryContext(recovery_token: string) {
  return request.post('/auth/mfa/recovery/context', { recovery_token })
}

export function startMfaRecoverySetup(recovery_token: string, current_password = '') {
  return request.post('/auth/mfa/recovery/setup/start', { recovery_token, current_password: current_password || null })
}

export function confirmMfaRecoverySetup(recovery_token: string, setup_token: string, code: string) {
  return request.post('/auth/mfa/recovery/setup/confirm', { recovery_token, setup_token, code })
}

export function verifyIpRecoveryMfa(mfa_ticket: string, code: string) {
  return request.post('/auth/ip-recovery/mfa/verify', { mfa_ticket, code })
}

export function allowCurrentIp(recovery_ticket: string, challenge_id: string, email_code: string) {
  return request.post('/auth/ip-recovery/allow-current', { recovery_ticket, challenge_id, email_code })
}

// 二步登录第一步：邮箱查候选租户
export function loginTenants(email: string) {
  return request.post('/auth/login/tenants', { email })
}

// 合并注册：建租户 + owner 账号
export function register(
  company_name: string,
  company_slug: string,
  nickname: string,
  email: string,
  password: string,
  registration_ticket: string,
) {
  return request.post('/auth/register', {
    company_name,
    company_slug,
    nickname,
    email,
    password,
    registration_ticket,
  })
}

export function sendRegisterCode(email: string, turnstile_token = '') {
  return request.post('/auth/register/code', { email, turnstile_token: turnstile_token || null })
}

export function verifyRegisterCode(email: string, challenge_id: string, code: string) {
  return request.post('/auth/register/verify', { email, challenge_id, code })
}

// 刷新 token
export function refreshToken(refresh_token: string) {
  return request.post('/auth/refresh', { refresh_token })
}

// 撤销当前 refresh token（接口幂等）
export function logout(refresh_token: string) {
  return request.post('/auth/logout', { refresh_token }, { timeout: 5000 })
}

export function establishPortalSession(refresh_token: string, remember: boolean) {
  return request.post('/auth/portal/session', { refresh_token, remember }, { withCredentials: true })
}

export function restorePortalSession() {
  return request.post('/auth/portal/restore', {}, { withCredentials: true })
}

export function logoutPortalSession() {
  return request.post('/auth/portal/logout', {}, { withCredentials: true, timeout: 5000 })
}

// 获取当前用户信息
export function getMe() {
  return request.get('/auth/me')
}

// 修改密码
export function changePassword(new_password: string) {
  return request.post('/auth/change-password', { new_password })
}

// 忘记密码
export function forgotPassword(email: string, turnstile_token = '') {
  return request.post('/auth/forgot-password', { email, turnstile_token: turnstile_token || null })
}

// 重置密码
export function resetPassword(email: string, code: string, new_password: string) {
  return request.post('/auth/reset-password', { email, code, new_password })
}

// 接受团队邀请并设置初始密码
export function acceptInvitation(token: string, password: string) {
  return request.post('/auth/invitations/accept', { token, password })
}

// 上传头像
export function uploadAvatar(file: File) {
  const fd = new FormData()
  fd.append('file', file)
  return request.post('/auth/avatar', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}
// 更新当前租户 slug（owner 可改可读别名）
export function updateTenantSlug(slug: string) {
  return request.put('/auth/tenant/slug', { slug })
}

export function adminLogin(username: string, password: string) {
  return request.post('/auth/login', { username, password })
}
